import {
  isEmpty, prop, pathOr, type,
} from 'ramda';
import { memo } from 'react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import {
  compose, withHandlers, withProps,
} from 'recompose';

import TaskItem from './taskItem';
import { IMAGES_SIZE } from '../../../../constants/ui';
import { newTaskSelectors } from '../../../../state/newTask';
import { projectActions, projectSelectors } from '../../../../state/project';
import { taskActions, taskSelectors } from '../../../../state/task';
import { tasksActions } from '../../../../state/tasks';
import { usersSelectors } from '../../../../state/users';
import { getUsersForSelect } from '../../../../state/users/selectors';
import { withTasksCRUDHandlers, withUserProfile } from '../../../../utils/enchancers';
import { getTimeStringBySeconds, getTimeUnitsFromTimeString } from '../../../../utils/helpers/dateHelpers';
import { withPermissionHandlers } from '../../../../utils/helpers/permissionHelpers';
import { getImageUrl } from '../../../../utils/helpers/requestHelpers';
import { getTaskProgress } from '../../../../utils/helpers/taskHelpers/common';
import { getFullName } from '../../../../utils/helpers/userHelpers';

const mapStateToProps = (state, { isTaskHasSprint, id }) => ({
  categories: projectSelectors.getProjectCategoriesTasks(state),
  getUserById: usersSelectors.getUser(state),
  data: isTaskHasSprint ? projectSelectors.getProjectSprintTask(state)(id)
    : projectSelectors.getBacklogTask(state)(id),
  isTaskPin: pathOr(false, [id, 'id'], projectSelectors.getPinnedTasksEntities(state)),
  agendaColumn: projectSelectors.getAgenda(state)((isTaskHasSprint
    ? projectSelectors.getProjectSprintTask(state)(id)
    : projectSelectors.getBacklogTask(state)(id)).status_id),
  membersList: projectSelectors.getProjectMembersList(state),
  allUsers: getUsersForSelect(state),
  statusesEntities: projectSelectors.getStatusesOfTasksEntities(state),
  statusesList: projectSelectors.getStatusesOfTasksList(state),
  modules: newTaskSelectors.getModulesForDropdown(state),
  addModuleIsPending: newTaskSelectors.getAddModuleTaskCrudPending(state),
  isAddTimeEntryPending: taskSelectors.getAddTimeEntryRequestPending(state),
});

const mapDispatchToProps = {
  updateTask: tasksActions.updateTaskRequest,
  addTimeEntry: taskActions.addTimeEntryRequest,
  getProjectTasksRequest: projectActions.getProjectTasksRequest,
};

const onOpenProfileAssigneeHandler = ({
  onSetUserProfile,
  data,
}) => () => onSetUserProfile(prop('assignee', data));

const onOpenProfileAuthorHandler = ({
  onSetUserProfile,
  data,
}) => () => onSetUserProfile(prop('author', data));

const getClassByStatus = (status) => {
  if (status === 0) {
    return 'project-task__row--new';
  }
  if (status === 4) {
    return 'project-task__row--closed';
  }
  return '';
};

const onEditHandler = ({
  onEditTask, projectId, id, data,
}) => () => {
  onEditTask({ taskId: id, projectId, clientId: data.client_id });
};

const onCopyHandler = ({
  data,
}) => () => {
  const url = `${window.location.host}/projects/${data.project_id}/task/${data.id}`;
  navigator.clipboard.writeText(url);
};

const onDeleteHandler = ({
  onDeleteTask, projectId, id, isTaskHasSprint,
}) => () => {
  onDeleteTask({ taskId: id, projectId, meta: ({ isHasSprint: isTaskHasSprint }) });
};

const onPinHandler = ({
  onPinTask, projectId, id,
}) => () => {
  onPinTask({ taskId: id, projectId });
};
const onUnpinHandler = ({
  onUnpinTask, projectId, id,
}) => () => {
  onUnpinTask({ taskId: id, projectId });
};

const onChangeAssigneeHandler = ({
  updateTask, data,
}) => ({ val }) => {
  updateTask({ id: data.id, assignee: val.id, project_id: data.project_id });
};

const onChangeStatusHandler = ({
  updateTask, data,
}) => ({ val }) => {
  updateTask({ id: data.id, status_id: val.id, project_id: data.project_id });
};

const onChangeModuleHandler = ({
  updateTask, data,
}) => (moduleId) => {
  updateTask({ id: data.id, project_module_id: moduleId, project_id: data.project_id });
};

const onChangeEstimatedTimeHandler = ({
  updateTask, data,
}) => (hours) => {
  updateTask({
    id: data.id,
    estimated_time: Math.round(hours * 3600),
    project_id: data.project_id,
  });
};

const onAddTimeEntryHandler = ({
  addTimeEntry, getProjectTasksRequest,
}) => (timeEntryData) => {
  addTimeEntry(timeEntryData, {
    callbacks: {
      success: () => {
        getProjectTasksRequest({
          projectId: timeEntryData.projectId,
          withRelated: 'tasks',
          limit: 100,
          offset: 0,
        });
      },
    },
  });
};


const enhance = compose(
  connect(mapStateToProps, mapDispatchToProps),
  withUserProfile,
  withTasksCRUDHandlers,
  withTranslation(['common']),
  withProps(({
    data, getUserById, membersList, allUsers, statusesEntities, statusesList,
    modules,
  }) => {
    const {
      estimated_time: estimatedTimeSeconds, spent_time, status, author, assigneeUser,
    } = data;

    const authorData = getUserById(author);
    const assigneeData = getUserById(assigneeUser);
    const assigneeAvatarData = prop('avatar', assigneeData);
    const assigneeAvatar = type(assigneeAvatarData) === 'Object'
      ? getImageUrl(assigneeAvatarData.id, IMAGES_SIZE.xs)
      : getImageUrl(assigneeAvatarData, IMAGES_SIZE.xs);

    const assigneeMembers = Object.values(allUsers)
      .filter(user => membersList.includes(user.value))
      .map(user => ({
        id: user.value,
        label: user.label || '',
      }));

    const statuses = statusesList
      .map(statusId => statusesEntities[statusId])
      .filter(Boolean)
      .map(statusItem => ({
        id: statusItem.id,
        label: statusItem.title,
      }));

    const module = modules.find(({ id }) => id === data.project_module_id);

    return {
      progress: getTaskProgress(data),
      classNameStatus: getClassByStatus(status),
      authorName: getFullName(authorData) || null,
      author: authorData,
      assignee: isEmpty(assigneeData) ? false : assigneeData,
      assigneeName: getFullName(assigneeData) || null,
      estimatedTime: `${getTimeUnitsFromTimeString(getTimeStringBySeconds(estimatedTimeSeconds), 'h')}h`,
      estimatedHours: Number(((estimatedTimeSeconds || 0) / 3600).toFixed(2)),
      spentTime: `${getTimeUnitsFromTimeString(getTimeStringBySeconds(spent_time), 'h')}h`,
      assigneeAvatar: assigneeAvatar || '',
      assigneeMembers,
      statuses,
      moduleName: module ? module.name : '',
    };
  }),
  withHandlers({
    onEdit: onEditHandler,
    onCopy: onCopyHandler,
    onDelete: onDeleteHandler,
    onPin: onPinHandler,
    onUnpin: onUnpinHandler,
    onOpenProfileAssignee: onOpenProfileAssigneeHandler,
    onOpenProfileAuthor: onOpenProfileAuthorHandler,
    onChangeAssignee: onChangeAssigneeHandler,
    onChangeStatus: onChangeStatusHandler,
    onChangeModule: onChangeModuleHandler,
    onChangeEstimatedTime: onChangeEstimatedTimeHandler,
    onAddTimeEntry: onAddTimeEntryHandler,
  }),
  withPermissionHandlers,
  memo,
);
export default enhance(TaskItem);
